import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';

import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';

import { BodyClassRenderer } from '@firestitch/html';
import { FsSidenavModule, Item, ItemActionType, SidenavConfig } from '@firestitch/sidenav';
import { FsStore } from '@firestitch/store';

import { merge } from 'rxjs';
import { distinctUntilChanged, filter, map, skip, tap } from 'rxjs/operators';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Account, Environment } from '@app/common';
import { AclService, SignoutService } from '@app/core';

import { LogoComponent } from '../../../../logo/components/logo/logo.component';
import { SidenavService } from '../../services/sidenav.service';
import { EnvironmentMenuComponent } from '../environment-menu/environment-menu.component';
import { HeaderComponent } from '../header/header.component';
import { NavbarComponent } from '../navbar/navbar.component';


@Component({
  selector: 'app-page-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    HeaderComponent,
    CdkScrollable,
    MatSidenavContainer,
    MatSidenav,
    RouterLink,
    LogoComponent,
    FsSidenavModule,
    MatSidenavContent,
    NavbarComponent,
    RouterOutlet,
  ],
})
export class SideNavComponent {

  public sidenavService = inject(SidenavService);

  @ViewChild(MatSidenav, { static: true })
  public sidenav: MatSidenav;

  public disableClose = computed(() => this.sidenavService.mode() !== 'over');
  public sideNavMain = signal<SidenavConfig>({ items: [] });
  public sideNavBottom = signal<SidenavConfig>({ items: [] });

  private _store = inject(FsStore);
  private _router = inject(Router);
  private _bodyClassRenderer = inject(BodyClassRenderer);
  private _signoutService = inject(SignoutService);
  private _aclService = inject(AclService);
  private _destroyRef = inject(DestroyRef);

  constructor() {
    this._init();
  }

  public get environment(): Environment {
    return this._store.get('environment');
  }

  public clickNav(): void {
    this.sidenavService.hide();
  }

  private _init(): void {
    this._bodyClassRenderer.addBodyClass('body-side-nav');
    this._observeResize();
    this._observeNavigation();
    this._buildSidenavBottom();
    this._observeStore();
  }

  // The service has already re-read the width (it subscribes first). Open the drawer here too
  // when the width wants it open: after a backdrop click the drawer is closed while opened()
  // still reads true, so the [opened] binding alone would not change.
  private _observeResize(): void {
    this.sidenavService.resize$
      .pipe(
        tap(() => {
          if (this.sidenavService.opened()) {
            this.sidenav.open();
          }
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  private _observeNavigation(): void {
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => window.location.pathname),
        distinctUntilChanged(),
        tap(() => {
          this._loadMainSidenav();
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  // skip(1): each observer starts with the value the menu was just built from
  private _observeStore(): void {
    merge(
      this._store.observe('permissions').pipe(skip(1)),
      this._store.observe('environment').pipe(skip(1)),
      this._store.observe('account').pipe(skip(1)),
    )
      .pipe(
        tap(() => {
          this._buildSidenavBottom();
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  private _loadMainSidenav(): void {
    const items: Item[] = [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: 'dashboard',
      },
    ];

    if (this._aclService.hasPermissionStaff(this.environment?.id)) {
      items.push({
        label: 'Staff',
        path: '/staff',
        icon: 'people',
      });
    }

    this.sideNavMain.set({ items });
  }

  private _buildSidenavBottom(): void {
    const account: Account = this._store.get('account');

    if (!account?.id) {
      this.sideNavBottom.set({ items: [] });

      return;
    }

    const items: Item[] = [this._getOrganizationItem()];

    if (this._aclService.isAdmin()) {
      items.push(this._getAdminItem());
    }

    if (this._aclService.hasPermissionAdmin()) {
      items.push(this._getSystemItem());
    }

    items.push(
      {
        label: 'Terms & Privacy',
        path: '/terms-privacy',
        icon: 'shield',
      },
      {
        label: `${account.name}`,
        superlabel: 'My Account',
        path: '/account',
        image: account.image.small,
      },
      {
        label: 'Sign Out',
        icon: 'exit_to_app',
        click: () => {
          this._signoutService.signout();
        },
      },
    );

    this.sideNavBottom.set({ items });
  }

  private _getAdminItem(): Item {
    return {
      label: 'Admin',
      path: '/admin',
      icon: 'settings',
      class: 'admin',
      items: [
        {
          label: 'Reports',
          path: '/admin/reports',
          exact: false,
        },
        {
          label: 'Accounts',
          path: '/admin/accounts',
          exact: false,
        },
        {
          label: 'Funds',
          path: '/admin/funds',
          exact: false,
        },
        {
          label: 'Messages',
          path: '/admin/message',
          exact: false,
        },
        {
          label: 'Organizations',
          path: '/admin/organizations',
          exact: false,
        },
        {
          label: 'Settings',
          path: '/admin/settings',
          exact: false,
        },
      ],
    };
  }

  private _getSystemItem(): Item {
    return {
      label: 'System',
      path: '/system',
      icon: 'personal_video',
      items: [
        {
          label: 'Settings',
          path: '/system/settings',
        },
        {
          label: 'Crons',
          path: '/system/crons',
        },
        {
          label: 'File Manager',
          path: '/system/files',
        },
        {
          label: 'Processes',
          path: '/system/processes',
        },
        {
          label: 'Logs',
          path: '/system/logs',
        },
        {
          label: 'Info',
          path: '/system/info',
        },
        {
          label: 'Blueprint',
          path: '/system/blueprint',
          exact: false,
        },
      ],
    };
  }

  private _getOrganizationItem(): Item {
    let organizationItem: Item = {
      label: 'No Organization',
      exact: true,
      actions: [],
    };

    // The ⋮ holds Switch Organization and an admin's Create New Organization, so an account that
    // can do neither (in no organization and not an admin) gets no ⋮ at all
    if (this._aclService.hasPermissionOrganization()) {
      organizationItem.actions
        .push({
          type: ItemActionType.Menu,
          icon: 'more_vert',
          menu: {
            component: EnvironmentMenuComponent,
          },
        });
    }

    const environment = this.environment;

    if (environment) {
      organizationItem = {
        ...organizationItem,
        superlabel: 'Organization',
        icon: 'account_balance',
      };

      if (this._aclService.hasPermissionAdmin()) {
        organizationItem = {
          ...organizationItem,
          path: `/organization/${environment.id}/settings`,
        };
      }

      organizationItem.label = environment.name;
    }

    return organizationItem;
  }
}
